import type { Signal } from '@bayer-int/portletce-shared';
import { pluckValue } from '@bayer-int/portletce-shared';
import { isUndefined, fromPairs } from 'lodash-es';

import type {
    FilterData,
    FilterIdToSignalMapping,
    PortletQuery,
    Specification,
    View,
    ViewId,
} from 'portlet/models';
import { isNotNullable } from 'portlet/common/utils';
import type {
    SelectDefinition,
    SelectNumberDefinition,
    SelectClearableDefinition,
    SelectNumberClearableDefinition,
    MultiSelectDefinition,
    InteractiveTitleSettingDefinition,
    AnyInteractiveTitleSettingDefinition,
} from 'header/types';
import { SettingFormInputDefinition } from 'header/types';

import type { Settings, SettingValueMapDiff } from '../index';
import { CustomSettingFormDefinitionInput } from '../customSettingFormDefinitionInput';
import type { CommonSettingDefinition } from './_common';
import { defaultCreateInteractiveTags, defaultCreateReplacementTags } from './_common';
import {
    SettingThatCanBeMandatoryKeys,
    SettingThatCanBeMandatoryLabels,
} from './_settingsThatCanBeMandatory';
import { TitleSettingDefinition, codec as TitleSpecCodec } from './titleSetting';
import { TitleAlignSettingDefinition, codec as TitleAlignSpecCodec } from './titleAlignSetting';
import { SubtitleSettingDefinition, codec as SubtitleSpecCodec } from './subtitleSetting';
import {
    ShortcutsColorSettingDefinition,
    codec as ShortcutsColorSpecCodec,
} from './shortcutsColorSetting';
import { CurrentViewSettingDefinition } from './currentViewSetting';
import { ScaleSettingDefinition, codec as ScaleSpecCodec } from './scaleSetting';
import { ScaleValuesSettingDefinition, codec as ScaleValuesSpecCodec } from './scaleValuesSetting';
import { DateTypeSettingDefinition, codec as DateTypeSpecCodec } from './dateTypeSetting';
import { DateRangeSettingDefinition, codec as DateRangeSpecCodec } from './dateRangeSetting';
import { InternalKeySettingDefinition, codec as InternalKeySpecCodec } from './internalKeySetting';
import { HierarchyIdSettingDefinition, codec as HierarchyIdSpecCodec } from './hierarchySetting';
import {
    LinkedHierarchiesSettingDefinition,
    codec as HierarchiesContextSpecCodec,
} from './linkedHierarchiesSetting';
import {
    SingleSelectSettingDefinition,
    codec as SingleSelectSpecCodec,
} from './singleSelectSetting';
import {
    LabelDisplayStyleSettingDefinition,
    codec as LabelDisplayStyleSpecCodec,
} from './labelDisplayStyleSetting';
import {
    MandatorySettingsSettingDefinition,
    codec as MandatorySettingsSpecCodec,
} from './mandatorySettings';
import {
    ResettableSettingsSettingDefinition,
    codec as ResettableSettingsSpecCodec,
} from './resettableSettings';
import {
    HierarchyVariantIdSettingDefinition,
    codec as HierarchyVariantIdSpecCodec,
} from './hierarchyVariantSetting';
import { DrillSettingDefinition, codec as DrillSpecCodec } from './drillSetting';
import { ConditionSettingDefinition, codec as ConditionSpecCodec } from './conditionSettings';
import {
    DimensionValuesSettingDefinition,
    codec as DimensionValuesSpecCodec,
} from './dimensionValuesSetting';
import { SmartViewSettingDefinition, codec as SmartViewSpecCodec } from './smartViewSetting';
import { ShortLabelsSettingDefinition, codec as ShortLabelsSpecCodec } from './shortLabelsSetting';
import {
    SmartViewValuesSettingDefinition,
    codec as SmartViewValuesSpecCodec,
} from './smartViewValuesSetting';
import { AutoDateSettingDefinition, codec as AutoDateSpecCodec } from './autoDateSetting';
import { AllowClearSettingDefinition, codec as AllowClearSpecCodec } from './allowClearSetting';
import { SortSettingDefinition, codec as SortCodec } from './sortSetting';
import { InputBWQuerySettingDefinition, codec as InputBWQueryCodec } from './inputBWQuery';
import { RoleSettingDefinition, codec as RoleSpecCodec } from './roleSetting';
import { BWSystemSettingDefinition, codec as BWSystemCodec } from './BWSystemSetting';
import { InfoProviderSettingDefinition, codec as InfoProviderCodec } from './infoProviderSetting';
import { ShortcutsSettingDefinition, codec as ShortcutsCodec } from './shortcutsSetting';
import { ViewFormSettingDefinition, codec as ViewFormCodec } from './viewFormSetting';
import {
    DatabricksConditionSettingDefinition,
    codec as DatabricksCondtitionCodec,
} from './databricksConditionSetting';
import {
    DatabricksDrillSettingDefinition,
    codec as DatabricksDrillCodec,
} from './databricksDrillSetting';
import {
    HierarchyValuesSettingDefinition,
    codec as HierarchyValuesCodec,
} from './hierarchyValuesSetting';
import {
    ApplyFilterOnSelectionSettingDefinition,
    codec as ApplyFilterOnSelectionCodec,
} from './applyFilterOnSelection';
import {
    HighlightOnFilterInCurrentDrillSettingDefinition,
    codec as HighlightOnFilterInCurrentDrillCodec,
} from './highlightOnFilterInCurrentDrill';
import {
    ApplyFilterOnExactDrillOnlyDefinition,
    codec as ApplyFilterOnExactDrillOnlyCodec,
} from './applyFilterOnExactDrillOnly';
import {
    ApplyFilterToUpstreamHierarchyLevelsDefinition,
    codec as ApplyFilterToUpstreamHierarchyLevelsCodec,
} from './applyFilterToUpstreamHierarchyLevels';

/**
 * Mapping between setting keys and their definitions.
 *
 * TODO: it should use computed properties, e.g.
 * ```
 *  [TitleSettingDefinition.key]: TitleSettingDefinition
 * ```
 * but for some reason it breaks ts intellisense in vsc (project still compiles)
 */
const SettingDefinitions = {
    TITLE: TitleSettingDefinition,
    SUBTITLE: SubtitleSettingDefinition,
    TITLE_ALIGN: TitleAlignSettingDefinition,
    VIEWID: CurrentViewSettingDefinition,
    SCALE: ScaleSettingDefinition,
    SCALE_VALUES: ScaleValuesSettingDefinition,
    DATE_TYPE: DateTypeSettingDefinition,
    DATE_RANGE: DateRangeSettingDefinition,
    INTERNAL_KEY: InternalKeySettingDefinition,
    DIMENSION_VALUES: DimensionValuesSettingDefinition,
    HIERARCHY_VALUES: HierarchyValuesSettingDefinition,
    HIERARCHY_ID: HierarchyIdSettingDefinition,
    HIERARCHY_VARIANT_ID: HierarchyVariantIdSettingDefinition,
    LINKED_HIERARCHY_IDS: LinkedHierarchiesSettingDefinition,
    SINGLE_SELECT: SingleSelectSettingDefinition,
    LABEL_DISPLAY_STYLE: LabelDisplayStyleSettingDefinition,
    MANDATORY_SETTINGS: MandatorySettingsSettingDefinition,
    RESETTABLE_SETTINGS: ResettableSettingsSettingDefinition,
    DRILL: DrillSettingDefinition,
    CONDITION: ConditionSettingDefinition,
    SMART_VIEW: SmartViewSettingDefinition,
    SMART_VIEW_VALUES: SmartViewValuesSettingDefinition,
    SHORT_LABELS: ShortLabelsSettingDefinition,
    AUTO_DATE: AutoDateSettingDefinition,
    ALLOW_CLEAR: AllowClearSettingDefinition,
    SORT: SortSettingDefinition,
    INPUT_BW_QUERY: InputBWQuerySettingDefinition,
    ROLE: RoleSettingDefinition,
    BW_SYSTEM: BWSystemSettingDefinition,
    INFO_PROVIDER: InfoProviderSettingDefinition,
    SHORTCUTS: ShortcutsSettingDefinition,
    SHORTCUTS_COLOR: ShortcutsColorSettingDefinition,
    VIEW_FORM: ViewFormSettingDefinition,
    APPLY_FILTER_ON_SELECTION: ApplyFilterOnSelectionSettingDefinition,
    HIGHLIGHT_ON_FILTER_IN_CURRENT_DRILL: HighlightOnFilterInCurrentDrillSettingDefinition,
    APPLY_FILTER_ON_EXACT_DRILL_ONLY: ApplyFilterOnExactDrillOnlyDefinition,
    APPLY_FILTER_TO_UPSTREAM_HIERARCHY_LEVELS: ApplyFilterToUpstreamHierarchyLevelsDefinition,
    DATABRICKS_CONDITION: DatabricksConditionSettingDefinition,
    DATABRICKS_DRILL: DatabricksDrillSettingDefinition,
} as const;

export const SettingKeys = pluckValue(SettingDefinitions, 'key');

/**
 * Map of setting keys to codecs which define the shape that the setting can be parsed from.
 * It is used in the `settings` field.
 */
export const SettingSpecValueCodecs = {
    TITLE: TitleSpecCodec,
    SUBTITLE: SubtitleSpecCodec,
    TITLE_ALIGN: TitleAlignSpecCodec,
    SCALE: ScaleSpecCodec,
    SCALE_VALUES: ScaleValuesSpecCodec,
    DATE_TYPE: DateTypeSpecCodec,
    DATE_RANGE: DateRangeSpecCodec,
    INTERNAL_KEY: InternalKeySpecCodec,
    DIMENSION_VALUES: DimensionValuesSpecCodec,
    HIERARCHY_VALUES: HierarchyValuesCodec,
    HIERARCHY_ID: HierarchyIdSpecCodec,
    HIERARCHY_VARIANT_ID: HierarchyVariantIdSpecCodec,
    LINKED_HIERARCHY_IDS: HierarchiesContextSpecCodec,
    SINGLE_SELECT: SingleSelectSpecCodec,
    LABEL_DISPLAY_STYLE: LabelDisplayStyleSpecCodec,
    MANDATORY_SETTINGS: MandatorySettingsSpecCodec,
    RESETTABLE_SETTINGS: ResettableSettingsSpecCodec,
    DRILL: DrillSpecCodec,
    CONDITION: ConditionSpecCodec,
    SMART_VIEW: SmartViewSpecCodec,
    SMART_VIEW_VALUES: SmartViewValuesSpecCodec,
    SHORT_LABELS: ShortLabelsSpecCodec,
    AUTO_DATE: AutoDateSpecCodec,
    ALLOW_CLEAR: AllowClearSpecCodec,
    SORT: SortCodec,
    INPUT_BW_QUERY: InputBWQueryCodec,
    ROLE: RoleSpecCodec,
    BW_SYSTEM: BWSystemCodec,
    INFO_PROVIDER: InfoProviderCodec,
    SHORTCUTS: ShortcutsCodec,
    SHORTCUTS_COLOR: ShortcutsColorSpecCodec,
    VIEW_FORM: ViewFormCodec,
    APPLY_FILTER_ON_SELECTION: ApplyFilterOnSelectionCodec,
    HIGHLIGHT_ON_FILTER_IN_CURRENT_DRILL: HighlightOnFilterInCurrentDrillCodec,
    APPLY_FILTER_ON_EXACT_DRILL_ONLY: ApplyFilterOnExactDrillOnlyCodec,
    APPLY_FILTER_TO_UPSTREAM_HIERARCHY_LEVELS: ApplyFilterToUpstreamHierarchyLevelsCodec,
    DATABRICKS_CONDITION: DatabricksCondtitionCodec,
    DATABRICKS_DRILL: DatabricksDrillCodec,
};

/**
 * Union of all setting form definitions
 */
export type SettingDefinition =
    | TitleSettingDefinition
    | SubtitleSettingDefinition
    | TitleAlignSettingDefinition
    | CurrentViewSettingDefinition
    | ScaleSettingDefinition
    | ScaleValuesSettingDefinition
    | DateTypeSettingDefinition
    | DateRangeSettingDefinition
    | InternalKeySettingDefinition
    | DimensionValuesSettingDefinition
    | HierarchyValuesSettingDefinition
    | HierarchyIdSettingDefinition
    | HierarchyVariantIdSettingDefinition
    | LinkedHierarchiesSettingDefinition
    | SingleSelectSettingDefinition
    | LabelDisplayStyleSettingDefinition
    | MandatorySettingsSettingDefinition
    | ResettableSettingsSettingDefinition
    | DrillSettingDefinition
    | ConditionSettingDefinition
    | SmartViewSettingDefinition
    | SmartViewValuesSettingDefinition
    | ShortLabelsSettingDefinition
    | AutoDateSettingDefinition
    | AllowClearSettingDefinition
    | SortSettingDefinition
    | InputBWQuerySettingDefinition
    | RoleSettingDefinition
    | BWSystemSettingDefinition
    | InfoProviderSettingDefinition
    | ShortcutsSettingDefinition
    | ShortcutsColorSettingDefinition
    | ViewFormSettingDefinition
    | ApplyFilterOnSelectionSettingDefinition
    | HighlightOnFilterInCurrentDrillSettingDefinition
    | ApplyFilterOnExactDrillOnlyDefinition
    | ApplyFilterToUpstreamHierarchyLevelsDefinition
    | DatabricksConditionSettingDefinition
    | DatabricksDrillSettingDefinition;

function toListOfDefinitions(
    definitions: Partial<SettingDefinitionMap>,
): SettingFormInputDefinition[] {
    const customSettingDefinitions = Object.values(definitions)
        .filter(isNotNullable)
        .filter(({ key }) => !SettingDefinition.isKnownSettingKey(key));

    return [
        definitions.TITLE,
        definitions.SUBTITLE,
        definitions.TITLE_ALIGN,
        definitions.VIEWID,
        definitions.SCALE,
        definitions.SCALE_VALUES,
        definitions.DATE_TYPE,
        definitions.DATE_RANGE,
        definitions.INTERNAL_KEY,
        definitions.DIMENSION_VALUES,
        definitions.HIERARCHY_VALUES,
        definitions.HIERARCHY_ID,
        definitions.HIERARCHY_VARIANT_ID,
        definitions.LINKED_HIERARCHY_IDS,
        definitions.SMART_VIEW,
        definitions.SMART_VIEW_VALUES,
        definitions.SHORT_LABELS,
        definitions.SINGLE_SELECT,
        definitions.LABEL_DISPLAY_STYLE,
        definitions.MANDATORY_SETTINGS,
        definitions.RESETTABLE_SETTINGS,
        definitions.DRILL,
        definitions.CONDITION,
        definitions.AUTO_DATE,
        definitions.ALLOW_CLEAR,
        definitions.SORT,
        definitions.INPUT_BW_QUERY,
        definitions.ROLE,
        definitions.BW_SYSTEM,
        definitions.INFO_PROVIDER,
        definitions.SHORTCUTS,
        definitions.SHORTCUTS_COLOR,
        definitions.VIEW_FORM,
        definitions.APPLY_FILTER_ON_SELECTION,
        definitions.HIGHLIGHT_ON_FILTER_IN_CURRENT_DRILL,
        definitions.APPLY_FILTER_ON_EXACT_DRILL_ONLY,
        definitions.APPLY_FILTER_TO_UPSTREAM_HIERARCHY_LEVELS,
        definitions.DATABRICKS_CONDITION,
        definitions.DATABRICKS_DRILL,
        ...customSettingDefinitions,
    ].filter(isNotNullable);
}

/**
 * Mapping from setting key to setting form definition.
 * For settings with unknown key it applies generic form input.
 */
export type SettingDefinitionMap = {
    [Key in SettingDefinition['key']]: SettingDefinition & { key: Key };
} & { [key: string]: SettingFormInputDefinition };

/**
 * Information about setting source which allow dashboard app to apply settings correctly
 * to portlets
 */
export type SettingsSource = { appliedBy?: 'userSelection' | 'mainConfig' | 'viewConfig' };

/**
 * SettingDefinition type extended with extra informations about setting
 */
export type SettingDefinitionWithExtraInformation = SettingDefinition & SettingsSource;

/**
 * Mapping from setting key to setting form value.
 */
export type SettingValueMap = {
    [Key in keyof SettingDefinitionMap]: SettingDefinitionMap[Key]['value'];
};

/**
 * Lookup between portlet view ids and settings available on each view
 */
export type SettingPerViewDictionary = Record<ViewId, Array<keyof SettingValueMap>>;

/**
 * Providing annotation because of:
 * > The inferred type of this node exceeds the maximum length the compiler will serialize. An explicit type annotation is needed.
 */
export const SettingDefinition: {
    toListOfDefinitions: typeof toListOfDefinitions;
    isKnownSettingKey: typeof isKnownSettingKey;
    isKnownSettingDefinition: typeof isKnownSettingDefinition;
    findOptionOfSelectSetting: typeof findOptionOfSelectSetting;
    findOptionOfMultiSelectSetting: typeof findOptionOfMultiSelectSetting;
    toValueMap: typeof toValueMap;
    toDefinitionMap: typeof toDefinitionMap;
    toDefinitionMapWithViewDictionary: typeof toDefinitionMapWithViewDictionary;
    applyDependencies: typeof applyDependencies;
    transformLabelToKey: typeof transformLabelToKey;
    mapSettingDefinitionMap: typeof mapSettingDefinitionMap;
    toReplacementTags: typeof toReplacementTags;
    toInteractiveReplacementTags: typeof toInteractiveReplacementTags;
    toSignals: typeof toSignals;
    applyInformationAboutSetting: typeof applyInformationAboutSetting;
    createDefinition: {
        [K in keyof typeof SettingDefinitions]: (typeof SettingDefinitions)[K]['createDefinitionWithOpts'];
    };
    createDefinitionFromSpec: {
        [K in keyof typeof SettingDefinitions]: (typeof SettingDefinitions)[K]['createDefinitionFromSpec'];
    };
} = {
    toListOfDefinitions,
    isKnownSettingKey,
    isKnownSettingDefinition,
    findOptionOfSelectSetting,
    findOptionOfMultiSelectSetting,
    toValueMap,
    toDefinitionMap,
    toDefinitionMapWithViewDictionary,
    applyDependencies,
    createDefinition: pluckValue(SettingDefinitions, 'createDefinitionWithOpts'),
    createDefinitionFromSpec: pluckValue(SettingDefinitions, 'createDefinitionFromSpec'),
    transformLabelToKey,
    mapSettingDefinitionMap,
    toReplacementTags,
    toInteractiveReplacementTags,
    toSignals,
    applyInformationAboutSetting,
};

function toValueMap(
    spec: Specification,
    view: View | null,
    settings: Partial<Settings> = spec.settings || {},
): Partial<SettingValueMap> {
    const keyedValues = Object.values(SettingDefinitions)
        .map(({ key, fromSetting }) => {
            const value = fromSetting(settings, spec, view);
            return isUndefined(value) ? null : { [key]: value };
        })
        .filter(isNotNullable);

    const keyedCustomSettingValues = Object.entries(settings)
        .map(([key, definitionInput]) =>
            CustomSettingFormDefinitionInput.codec.is(definitionInput)
                ? CustomSettingFormDefinitionInput.toSettingFormInputDefinition(
                      key,
                      definitionInput,
                  )
                : null,
        )
        .filter(isNotNullable)
        .map(definition => ({ [definition.key]: definition.value }));

    return Object.assign({}, ...keyedValues, ...keyedCustomSettingValues);
}

function toDefinitionMap(
    spec: Specification,
    view: View | null,
    settings: Partial<Settings> = spec.settings || {},
): Partial<SettingDefinitionMap> {
    const keyedDefinitions = Object.values(SettingDefinitions)
        .map(({ key, createDefinitionFromSpec }) => {
            const value = createDefinitionFromSpec(settings, spec, view);

            return isUndefined(value) ? null : { [key]: value };
        })
        .filter(isNotNullable);
    const keyedCustomDefinitions = Object.entries(settings)
        .map(([key, definitionInput]) =>
            CustomSettingFormDefinitionInput.codec.is(definitionInput)
                ? CustomSettingFormDefinitionInput.toSettingFormInputDefinition(
                      key,
                      definitionInput,
                  )
                : null,
        )
        .filter(isNotNullable)
        .map(definition => ({ [definition.key]: definition }));

    return Object.assign({}, ...keyedDefinitions, ...keyedCustomDefinitions);
}

function toDefinitionMapWithViewDictionary(
    spec: Specification,
    views: View[],
    currentViewId: ViewId | null = null,
): [Partial<SettingDefinitionMap>, SettingPerViewDictionary] {
    const definitionsPerView = views.map(view => ({
        id: view.id,
        definitions: toDefinitionMap(spec, view, { ...spec.settings, ...view.settings }),
    }));

    const definitionsOnCurrentView = definitionsPerView.find(({ id }) => id === currentViewId);

    const allDefinitions = Object.assign(
        {},
        ...definitionsPerView.map(({ definitions }) => definitions),
        // assign definitions from current view last, so that they overwrite ones from other views
        // (if defined on multiple views)
        definitionsOnCurrentView?.definitions,
    );

    const definitionsPerViewDictionary = definitionsPerView.reduce((acc, { id, definitions }) => {
        acc[id] = Object.keys(definitions);
        return acc;
    }, {} as SettingPerViewDictionary);

    return [allDefinitions, definitionsPerViewDictionary];
}

function applyDependencies(
    specification: Specification,
    view: View | null,
    valueDiff: SettingValueMapDiff,
    current: Partial<SettingValueMap>,
    previous: Partial<SettingValueMap>,
    definitions: Partial<SettingDefinitionMap>,
    previousDefinitions: Partial<SettingDefinitionMap>,
    filters: FilterData[],
    listenToSignalMapping: FilterIdToSignalMapping,
): SettingValueMapDiff {
    const commonSettingDefinitions = Object.values(SettingDefinitions) as Array<
        CommonSettingDefinition<keyof typeof SettingKeys, SettingDefinition>
    >;

    const keyedValues: SettingValueMapDiff[] = commonSettingDefinitions
        .map(setting => {
            const currentDefinition = definitions[setting.key];
            const currentValue = current[setting.key];
            if (!currentDefinition || !setting.applySettingDependency) return null;

            const newValue = setting.applySettingDependency({
                ...setting,
                currentDefinition,
                diff: valueDiff,
                specification,
                view,
                settings: current,
                previousSettings: previous,
                filters,
                listenToSignalMapping,
            });

            return isUndefined(newValue)
                ? null
                : { [setting.key]: { previous: currentValue, current: newValue.value } };
        })
        .filter(isNotNullable);

    const customKeyedValues: SettingValueMapDiff[] = Object.values(definitions)
        .filter(isNotNullable)
        .filter(d => !isKnownSettingDefinition(d))
        .map(currentDefinition => {
            const currentValue = current[currentDefinition.key];
            const previousDefinition = previousDefinitions[currentDefinition.key];

            if (isUndefined(previousDefinition)) return null;
            if (isUndefined(currentValue)) return null;

            const newValue = SettingFormInputDefinition.tryApplySettingValueToNewDefinition(
                previousDefinition,
                currentValue,
                currentDefinition,
            );

            return currentValue === newValue
                ? null
                : { [currentDefinition.key]: { previous: currentValue, current: newValue } };
        })
        .filter(isNotNullable);

    return Object.assign({}, ...keyedValues, ...customKeyedValues);
}

function isKnownSettingKey(key: string): key is SettingDefinition['key'] {
    return key in SettingDefinitions;
}

function isKnownSettingDefinition(
    definition: SettingFormInputDefinition,
): definition is SettingDefinition {
    return Object.values(SettingDefinitions).some(({ key }) => key === definition.key);
}

function mapSettingDefinitionMap(
    definitions: Partial<SettingDefinitionMap>,
    fn: (def: SettingDefinition) => SettingDefinition,
): Partial<SettingDefinitionMap> {
    return Object.values(definitions).reduce((acc, definition) => {
        if (!definition) return acc;

        acc[definition.key] = SettingDefinition.isKnownSettingDefinition(definition)
            ? fn(definition)
            : definition;

        return acc;
    }, {} as Partial<SettingDefinitionMap>);
}

function toReplacementTags(definitions: Partial<SettingDefinitionMap>): Record<string, string> {
    type CreateReplacementTags = <T extends SettingFormInputDefinition>(
        definition: T,
    ) => Record<string, string>;

    return Object.values(definitions).reduce((acc, definition) => {
        if (!definition) return acc;

        const createReplacements = isKnownSettingDefinition(definition)
            ? // `SettingDefinitions[definition.key]` is a union of objects with functions of different argument types
              // which depend on the `definition.key`. Since at compile-time we cannot analyze which variant it will be,
              // the resulting parameter type is narrowed to `never`. Therefore we have to cast.
              // better explanation:  https://stackoverflow.com/a/71340801
              (SettingDefinitions[definition.key].replacementTags as CreateReplacementTags)
            : defaultCreateReplacementTags;

        return Object.assign(acc, createReplacements(definition));
    }, {});
}

function toInteractiveReplacementTags(
    definitions: Partial<SettingDefinitionMap>,
): Record<string, InteractiveTitleSettingDefinition> {
    type CreateInteractiveReplacementTags = <T extends SettingFormInputDefinition>(
        definition: T,
    ) => Record<string, AnyInteractiveTitleSettingDefinition>;

    return Object.values(definitions).reduce((acc, definition) => {
        if (!definition) return acc;

        const createReplacements = isKnownSettingDefinition(definition)
            ? // `SettingDefinitions[definition.key]` is a union of objects with functions of different argument types
              // which depend on the `definition.key`. Since at compile-time we cannot analyze which variant it will be,
              // the resulting parameter type is narrowed to `never`. Therefore we have to cast.
              // better explanation:  https://stackoverflow.com/a/71340801

              (SettingDefinitions[definition.key]
                  .interactiveTags as CreateInteractiveReplacementTags)
            : defaultCreateInteractiveTags;

        return Object.assign(acc, createReplacements(definition));
    }, {});
}

function toSignals(
    settings: Partial<SettingValueMap>,
    settingDefinitions: Partial<SettingDefinitionMap>,
    mergedSettings: Partial<SettingValueMap>,
    currentViewSignals: Signal[],
    filters: FilterData[],
    queryMetadata: PortletQuery | null,
    filterToSignalName: FilterIdToSignalMapping,
): Signal[] {
    return Object.entries(settings).flatMap(([name, value]) => {
        const defaultSignal: Signal[] = [{ name, value }];
        const definition = settingDefinitions[name];

        if (!definition) return defaultSignal;

        const definitionWithValue = { ...definition, value } as SettingDefinition;
        const settingInterface = SettingDefinitions[
            definitionWithValue.key
        ] as CommonSettingDefinition<string, SettingDefinition>;

        return SettingDefinition.isKnownSettingDefinition(definitionWithValue)
            ? settingInterface.toSignals(definitionWithValue, {
                  mergedSettings,
                  currentViewSignals,
                  filters,
                  queryMetadata,
                  filterToSignalName,
              })
            : defaultSignal;
    });
}

/**
 * Suitable for retrieving the selected option (whole object, not just key) of the setting.
 */
function findOptionOfSelectSetting<
    Select extends
        | SelectDefinition
        | SelectNumberDefinition
        | SelectClearableDefinition
        | SelectNumberClearableDefinition,
>(setting: Select, value: Select['value'] = setting.value): Select['options'][number] | null {
    // https://github.com/microsoft/TypeScript/issues/36390
    const arr = setting.options as Array<{ key: unknown }>;

    return (arr.find(({ key }) => key === value) || null) as Select['options'][number] | null;
}

function findOptionOfMultiSelectSetting<Select extends MultiSelectDefinition>(
    setting: Select,
    value: Select['value'] = setting.value,
): Select['options'] {
    return setting.options.filter(({ key }) => value.includes(key));
}

function transformLabelToKey(label: string) {
    const labelKeyPairs = fromPairs(
        Object.values(SettingDefinitions).map(({ key, label }) => [label, key]),
    );

    return labelKeyPairs[label] || label;
}

function applyInformationAboutSetting(
    setting: SettingFormInputDefinition,
    settings: Partial<SettingValueMap>,
    computedDefaultSettings: Partial<SettingValueMap>,
): SettingDefinitionWithExtraInformation {
    if (!isUndefined(settings[setting.key])) {
        return { ...setting, appliedBy: 'userSelection' } as SettingDefinitionWithExtraInformation;
    }
    if (!isUndefined(computedDefaultSettings[setting.key]))
        return { ...setting, appliedBy: 'viewConfig' } as SettingDefinitionWithExtraInformation;
    return { ...setting, appliedBy: 'mainConfig' } as SettingDefinitionWithExtraInformation;
}

export {
    TitleSettingDefinition,
    SubtitleSettingDefinition,
    TitleAlignSettingDefinition,
    CurrentViewSettingDefinition,
    ScaleSettingDefinition,
    ScaleValuesSettingDefinition,
    DateTypeSettingDefinition,
    DateRangeSettingDefinition,
    InternalKeySettingDefinition,
    DimensionValuesSettingDefinition,
    HierarchyValuesSettingDefinition,
    HierarchyIdSettingDefinition,
    HierarchyVariantIdSettingDefinition,
    LinkedHierarchiesSettingDefinition,
    SingleSelectSettingDefinition,
    LabelDisplayStyleSettingDefinition,
    MandatorySettingsSettingDefinition,
    ResettableSettingsSettingDefinition,
    SettingThatCanBeMandatoryKeys,
    SettingThatCanBeMandatoryLabels,
    DrillSettingDefinition,
    ConditionSettingDefinition,
    SmartViewSettingDefinition,
    SmartViewValuesSettingDefinition,
    ShortLabelsSettingDefinition,
    AutoDateSettingDefinition,
    AllowClearSettingDefinition,
    SortSettingDefinition,
    InputBWQuerySettingDefinition,
    RoleSettingDefinition,
    ViewFormSettingDefinition,
    ApplyFilterOnSelectionSettingDefinition,
    HighlightOnFilterInCurrentDrillSettingDefinition,
    ApplyFilterOnExactDrillOnlyDefinition,
    ApplyFilterToUpstreamHierarchyLevelsDefinition,
};
export { DRILL_BY_NONE_OPTION } from './drillSetting';
